import { useAppStore } from '@store'
import type { Store } from '@types'

let batchDepth = 0
let savedFrameloop: Store['frameloop'] | null = null

export function isLinkedVisualBatchActive(): boolean {
  return batchDepth > 0
}

let lateralPlacementDepth = 0

/** Colocación de laterales: sin spinner ni frameloop; evita cambios de selección en drawItem. */
export function isLateralPlacementBatchActive(): boolean {
  return lateralPlacementDepth > 0
}

export async function runLateralPlacementBatch<T>(work: () => Promise<T>): Promise<T> {
  lateralPlacementDepth++
  try {
    return await work()
  } finally {
    lateralPlacementDepth--
  }
}

export type LinkedBatchReporter = (message: string, current?: number, total?: number) => void

const DEFAULT_MESSAGE = 'Aplicando cambios...'

function formatBatchMessage(message: string): string {
  return message
}

/**
 * Agrupa cambios linked (GLB, texturas, hijos) en una sola pasada visual:
 * - frameloop 'never' → sin renders intermedios
 * - spinner global
 * - un solo repaint al finalizar
 */
export async function runLinkedVisualBatch<T>(
  work: (report: LinkedBatchReporter) => Promise<T>,
  options?: { message?: string },
): Promise<T> {
  const isOutermost = batchDepth === 0
  batchDepth++

  if (isOutermost) {
    const state = useAppStore.getState()
    savedFrameloop = state.frameloop
    useAppStore.setState((s) => {
      s.frameloop = 'never'
    })
    state.setGlobalSpinner?.(true, options?.message ?? DEFAULT_MESSAGE)
  }

  const report: LinkedBatchReporter = (message) => {
    useAppStore
      .getState()
      .setGlobalSpinner?.(true, formatBatchMessage(message))
  }

  try {
    return await work(report)
  } finally {
    batchDepth--
    if (isOutermost) {
      const restoreFrameloop =
        savedFrameloop === 'never' || savedFrameloop == null ? 'demand' : savedFrameloop
      savedFrameloop = null

      useAppStore.setState((s) => {
        s.frameloop = restoreFrameloop
        s.shouldUpdateCamera = true
      })
      useAppStore.getState().setGlobalSpinner?.(false)

      try {
        const r3f = typeof window !== 'undefined' ? (window as any).r3f : undefined
        requestAnimationFrame(() => {
          r3f?.invalidate?.()
          requestAnimationFrame(() => r3f?.invalidate?.())
        })
      } catch {
        // ignore
      }

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('linkedVisualBatchComplete'))
      }
    }
  }
}
